// ==========================================================================
// SignalFix v2 — Central element taxonomy
// --------------------------------------------------------------------------
// Single source of truth for the elements SignalFix recognises and draws on
// the map. Everything else (markers, legend, category filters, sample data)
// reads from here so the taxonomy is defined in exactly one place.
//
// Categories (from the v2 spec):
//   - 'vertical'   : segnaletica verticale (road signs, handled by signTypes)
//   - 'horizontal' : segnaletica orizzontale (drawn on the carriageway at zoom)
//   - 'bbox'       : elementi_strada_bbox (point elements, dedicated icons)
//   - 'seg'        : elementi_strada_seg (segmented / extended elements)
// ==========================================================================

// Brand palette (mirrors --primary-color / --secondary-color in the CSS).
const COLORS = {
  manhole: "#3a3a3a",
  grate: "#9aa0a6",
  danger: "#e23b2e",
  pothole: "#ff7a00",
  bollard: "#d0342c",
  bin: "#3f9c3a",
  billboard: "#7c5cff",
  lamp: "#f4c20d",
  trafficLight: "#1f1f1f",
  newJersey: "#c2c2c2",
  sidewalk: "#3b3b3b",
  guardrail: "#8a9199",
  pole: "#6b7280",
};

// --------------------------------------------------------------------------
// Inline SVG icon builders. Each returns a self-contained <svg> string sized
// to `size` px, so it can be dropped straight into a Leaflet divIcon or turned
// into a data URI for <img> usage. The drawings intentionally echo the shape
// descriptions in the spec so each element is distinguishable in the 2D view.
// --------------------------------------------------------------------------
const svg = (size, inner) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 32 32" style="display:block">${inner}</svg>`;

const ICONS = {
  // Dark circle recalling a manhole seen from above.
  tombino: (s = 26) =>
    svg(
      s,
      `<circle cx="16" cy="16" r="13" fill="${COLORS.manhole}" stroke="#fff" stroke-width="2"/>
       <circle cx="16" cy="16" r="8" fill="none" stroke="#6b6b6b" stroke-width="1.5"/>
       <line x1="16" y1="8" x2="16" y2="24" stroke="#6b6b6b" stroke-width="1.2"/>`
    ),
  // Rectangle with a grid pattern -> grate / gully, distinct from the manhole.
  griglia_caditoia: (s = 26) =>
    svg(
      s,
      `<rect x="4" y="8" width="24" height="16" rx="3" fill="${COLORS.grate}" stroke="#fff" stroke-width="2"/>
       <line x1="10" y1="9" x2="10" y2="23" stroke="#4d4d4d" stroke-width="1.3"/>
       <line x1="16" y1="9" x2="16" y2="23" stroke="#4d4d4d" stroke-width="1.3"/>
       <line x1="22" y1="9" x2="22" y2="23" stroke="#4d4d4d" stroke-width="1.3"/>`
    ),
  // Red warning triangle -> speed bump.
  dosso: (s = 26) =>
    svg(
      s,
      `<path d="M16 4 L29 27 L3 27 Z" fill="#fff" stroke="${COLORS.danger}" stroke-width="2.5" stroke-linejoin="round"/>
       <path d="M8 23 q8 -8 16 0" fill="none" stroke="${COLORS.danger}" stroke-width="2.4" stroke-linecap="round"/>`
    ),
  // Orange irregular "eye" blob -> pothole anomaly.
  buca: (s = 26) =>
    svg(
      s,
      `<path d="M4 16 C6 9 12 6 16 6 C22 6 28 10 28 16 C28 22 21 26 16 26 C10 26 3 23 4 16 Z" fill="${COLORS.pothole}" stroke="#fff" stroke-width="2"/>
       <ellipse cx="16" cy="16" rx="5" ry="3.4" fill="#4a2400"/>`
    ),
  // Small red/white capsule -> bollard seen from above.
  paletto_dissuasore: (s = 22) =>
    svg(
      s,
      `<rect x="12" y="5" width="8" height="22" rx="4" fill="${COLORS.bollard}" stroke="#fff" stroke-width="2"/>
       <rect x="12" y="12" width="8" height="4" fill="#fff"/>`
    ),
  // Green bin -> street furniture.
  bidone: (s = 24) =>
    svg(
      s,
      `<rect x="9" y="10" width="14" height="17" rx="2" fill="${COLORS.bin}" stroke="#fff" stroke-width="2"/>
       <rect x="7" y="6" width="18" height="4" rx="2" fill="${COLORS.bin}" stroke="#fff" stroke-width="1.5"/>
       <line x1="13" y1="14" x2="13" y2="23" stroke="#e7ffe4" stroke-width="1.3"/>
       <line x1="19" y1="14" x2="19" y2="23" stroke="#e7ffe4" stroke-width="1.3"/>`
    ),
  // Purple panel on a post -> billboard.
  cartellone_pubblicitario: (s = 24) =>
    svg(
      s,
      `<rect x="6" y="6" width="20" height="13" rx="2" fill="${COLORS.billboard}" stroke="#fff" stroke-width="2"/>
       <rect x="14" y="19" width="4" height="8" fill="${COLORS.pole}"/>`
    ),
  // Yellow lamp on a post -> light pole.
  palo_luce: (s = 24) =>
    svg(
      s,
      `<circle cx="16" cy="9" r="6" fill="${COLORS.lamp}" stroke="#fff" stroke-width="2"/>
       <rect x="14.5" y="14" width="3" height="14" fill="${COLORS.pole}"/>`
    ),
  // Generic pole dot -> sign / advertising pole when shown standalone.
  palo_cartello: (s = 22) =>
    svg(
      s,
      `<circle cx="16" cy="9" r="6" fill="#eab308" stroke="#fff" stroke-width="2"/>
       <rect x="14.5" y="14" width="3" height="14" fill="${COLORS.pole}"/>`
    ),
  palo_pubblicita: (s = 22) =>
    svg(
      s,
      `<circle cx="16" cy="9" r="6" fill="${COLORS.billboard}" stroke="#fff" stroke-width="2"/>
       <rect x="14.5" y="14" width="3" height="14" fill="${COLORS.pole}"/>`
    ),
  // Mini traffic light -> lanterna semaforica.
  lanterna_semaforica: (s = 26) =>
    svg(
      s,
      `<rect x="10" y="4" width="12" height="24" rx="4" fill="${COLORS.trafficLight}" stroke="#fff" stroke-width="2"/>
       <circle cx="16" cy="10" r="2.4" fill="#ff4d4f"/>
       <circle cx="16" cy="16" r="2.4" fill="#ffd400"/>
       <circle cx="16" cy="22" r="2.4" fill="#37d067"/>`
    ),
};

// Turn an SVG string into a data URI usable as an <img src>.
export const svgToDataUri = (svgString) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;

// --------------------------------------------------------------------------
// Stroke styles for segmented elements (rendered as Leaflet polylines).
// The shape (width + dash) is what distinguishes them, per the spec, not just
// the colour: New Jersey = wide modular band, sidewalk = solid dark band,
// guardrail = thin line with regular posts.
// --------------------------------------------------------------------------
const STROKES = {
  new_jersey: { color: COLORS.newJersey, weight: 9, opacity: 0.95, dashArray: "10 4", lineCap: "butt" },
  marciapiede: { color: COLORS.sidewalk, weight: 7, opacity: 0.9, lineCap: "round" },
  guardrail: { color: COLORS.guardrail, weight: 3.5, opacity: 0.95, dashArray: "2 7", lineCap: "round" },
};

// --------------------------------------------------------------------------
// Element registry. `minZoom` follows the spec's progressive-visibility rules:
// secondary / dense elements only appear once the user is close enough.
// --------------------------------------------------------------------------
export const SIGNAL_ELEMENTS = {
  // ---- Point elements (elementi_strada_bbox) ----
  lanterna_semaforica: {
    category: "bbox",
    label: "Lanterna semaforica",
    legendGroup: "bbox",
    color: COLORS.trafficLight,
    minZoom: 14,
    iconSvg: ICONS.lanterna_semaforica,
  },
  tombino: {
    category: "bbox",
    label: "Tombino",
    legendGroup: "bbox",
    color: COLORS.manhole,
    minZoom: 17,
    iconSvg: ICONS.tombino,
  },
  griglia_caditoia: {
    category: "bbox",
    label: "Griglia / Caditoia",
    legendGroup: "bbox",
    color: COLORS.grate,
    minZoom: 17,
    iconSvg: ICONS.griglia_caditoia,
  },
  dosso: {
    category: "bbox",
    label: "Dosso",
    legendGroup: "bbox",
    color: COLORS.danger,
    minZoom: 15,
    iconSvg: ICONS.dosso,
  },
  buca: {
    category: "bbox",
    label: "Buca",
    legendGroup: "bbox",
    color: COLORS.pothole,
    minZoom: 15,
    iconSvg: ICONS.buca,
  },
  paletto_dissuasore: {
    category: "bbox",
    label: "Paletto dissuasore",
    legendGroup: "bbox",
    color: COLORS.bollard,
    minZoom: 17,
    iconSvg: ICONS.paletto_dissuasore,
  },
  bidone: {
    category: "bbox",
    label: "Bidone",
    legendGroup: "bbox",
    color: COLORS.bin,
    minZoom: 17,
    iconSvg: ICONS.bidone,
  },
  cartellone_pubblicitario: {
    category: "bbox",
    label: "Cartellone pubblicitario",
    legendGroup: "bbox",
    color: COLORS.billboard,
    minZoom: 17,
    iconSvg: ICONS.cartellone_pubblicitario,
  },

  // ---- Segmented elements (elementi_strada_seg) ----
  palo_luce: {
    category: "seg",
    label: "Palo luce",
    legendGroup: "seg",
    color: COLORS.lamp,
    minZoom: 17,
    iconSvg: ICONS.palo_luce, // shown as a point (spec: too small to segment)
  },
  palo_cartello: {
    category: "seg",
    label: "Palo cartello",
    legendGroup: "seg",
    color: COLORS.pole,
    minZoom: 17,
    iconSvg: ICONS.palo_cartello,
  },
  palo_pubblicita: {
    category: "seg",
    label: "Palo pubblicità",
    legendGroup: "seg",
    color: COLORS.billboard,
    minZoom: 17,
    iconSvg: ICONS.palo_pubblicita,
  },
  new_jersey: {
    category: "seg",
    label: "New Jersey",
    legendGroup: "seg",
    color: COLORS.newJersey,
    minZoom: 16,
    stroke: STROKES.new_jersey,
  },
  marciapiede: {
    category: "seg",
    label: "Marciapiede",
    legendGroup: "seg",
    color: COLORS.sidewalk,
    minZoom: 16,
    stroke: STROKES.marciapiede,
  },
  guardrail: {
    category: "seg",
    label: "Guardrail",
    legendGroup: "seg",
    color: COLORS.guardrail,
    minZoom: 16,
    stroke: STROKES.guardrail,
  },
};

// Normalise an incoming sign `type` string (e.g. "griglia/caditoia",
// "paletto-dissuasore") to a registry key. Note: the v2 element keys never
// carry the "-so" (segnaletica orizzontale) suffix, so it is intentionally
// NOT stripped here — that keeps existing "-so" marking types (e.g. dosso-so)
// from colliding with v2 point elements like "dosso".
export const normalizeElementKey = (type = "") =>
  String(type)
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
    .replace(/\//g, "_")
    .replace(/à/g, "a");

// Look up an element definition from a raw sign `type`.
export const getElement = (type) => SIGNAL_ELEMENTS[normalizeElementKey(type)] || null;

export const isSegmentedElement = (type) => getElement(type)?.category === "seg" && !!getElement(type)?.stroke;

// --------------------------------------------------------------------------
// Legend groups shown in the map legend (order matters for display).
// --------------------------------------------------------------------------
export const LEGEND_GROUPS = [
  {
    key: "bbox",
    titleKey: "signalFix.legend.pointElements",
    items: [
      "lanterna_semaforica",
      "tombino",
      "griglia_caditoia",
      "dosso",
      "buca",
      "paletto_dissuasore",
      "bidone",
      "cartellone_pubblicitario",
    ],
  },
  {
    key: "seg",
    titleKey: "signalFix.legend.segmentedElements",
    items: ["new_jersey", "marciapiede", "guardrail", "palo_luce", "palo_cartello", "palo_pubblicita"],
  },
];

// --------------------------------------------------------------------------
// Category filters (the toggle list in the filter card / mockups).
// Each maps to a predicate over a raw sign object so the map can be filtered
// by high-level family instead of individual type.
// --------------------------------------------------------------------------
const isVerticalSign = (sign) => !sign.Segnaletica && !getElement(sign.type);
const isHorizontalSign = (sign) => sign.Segnaletica === true && !getElement(sign.type);

const INFRA_KEYS = new Set([
  "tombino",
  "griglia_caditoia",
  "lanterna_semaforica",
  "paletto_dissuasore",
  "new_jersey",
  "marciapiede",
  "guardrail",
  "palo_luce",
  "palo_cartello",
]);
const ANOMALY_KEYS = new Set(["dosso", "buca"]);
const FURNITURE_KEYS = new Set(["bidone", "cartellone_pubblicitario", "palo_pubblicita"]);

const inSet = (set) => (sign) => {
  const key = normalizeElementKey(sign.type);
  return set.has(key);
};

export const CATEGORY_FILTERS = [
  { key: "all", labelKey: "signalFix.categories.all", match: () => true, master: true },
  { key: "vertical", labelKey: "signalFix.categories.vertical", match: isVerticalSign },
  { key: "infrastructure", labelKey: "signalFix.categories.infrastructure", match: inSet(INFRA_KEYS) },
  { key: "anomalies", labelKey: "signalFix.categories.anomalies", match: inSet(ANOMALY_KEYS) },
  { key: "furniture", labelKey: "signalFix.categories.furniture", match: inSet(FURNITURE_KEYS) },
  { key: "horizontal", labelKey: "signalFix.categories.horizontal", match: isHorizontalSign },
];

// Given the set of enabled category keys, decide whether a sign is visible.
export const isSignInEnabledCategories = (sign, enabledKeys) => {
  if (enabledKeys.has("all")) return true;
  return CATEGORY_FILTERS.some((c) => !c.master && enabledKeys.has(c.key) && c.match(sign));
};
